import { Alert, Card, Popover, Stack, TextInput } from "@mantine/core";
import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { CirclePicker } from "react-color";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import { getMyAssociations } from "../../data/actions/associationActions";
import { getAllPayrates } from "../../data/actions/ratesActions";
import CompetitionApi from "../../data/api/competitionApi";
import { IState } from "../../data/reducers/state";
import { IAssociation } from "../../types/association/Association";
import { ICompetition } from "../../types/competition/Competition";
import { IPayRate } from "../../types/payrates/PayRate";
import { ITag, TagType } from "../../types/tags/tag";
import { toDateTime, toDateTimeNullable } from "../../util/date+helper";
import { CenteredLoader } from "../common/loader";
import { SSTable } from "../common/TableData";
import AssociationSelector from "../management/Association/AssociationSelector";

export function hashCode(str: string): number {
  let h: number = 0;
  for (let i = 0; i < str.length; i++) {
    h = 31 * h + str.charCodeAt(i);
  }
  return h & 0xffffffff;
}

export interface ICompetitionEditorProps {
  competitionId?: string | null;
  associations: IAssociation[];
  payrates: { [id: string]: IPayRate };

  actions: {
    getMyAssociations: () => Promise<IAssociation[]>;
    getAllPayrates: Function;
  };
}

export interface ICompetitionEditorState {
  competitionId: string | null;
  competition?: ICompetition;
  association?: IAssociation;

  showCurrentPayrates: boolean;

  editingColor: boolean;
  tagInputValue: string;

  loading: boolean;
  error?: string | null;
}

class CompetitionEditor extends React.PureComponent<
  ICompetitionEditorProps,
  ICompetitionEditorState
> {
  constructor(props, context) {
    super(props, context);

    this.state = {
      showCurrentPayrates: true,
      loading: true,
      competitionId: null,
      editingColor: false,
      tagInputValue: "",
    };

    this.onPayrateChange = this.onPayrateChange.bind(this);
    this.onColorPickerChanged = this.onColorPickerChanged.bind(this);
    this._handleTagKeyDown = this._handleTagKeyDown.bind(this);
    this.tagInputValueChanged = this.tagInputValueChanged.bind(this);
    this.addNewPayrate = this.addNewPayrate.bind(this);
  }

  componentDidMount() {
    this.checkAndGetIfNewCompetition(this.props);
    this.props.actions.getAllPayrates();
  }

  componentWillReceiveProps(props) {
    this.checkAndGetIfNewCompetition(props);
  }

  checkAndGetIfNewCompetition(props: ICompetitionEditorProps) {
    const { competitionId } = props;
    if (!competitionId) return;
    if (this.state.competitionId === competitionId) return;
    this.setState({ competitionId });

    this.setState({ loading: true });
    CompetitionApi.getCompetitionById(competitionId)
      .then(({ competition, association }) =>
        this.setState({ competition, association, loading: false }),
      )
      .catch((er) => this.setState({ error: er.message }));
    this.props.actions.getMyAssociations().catch(console.log); // swallow it for now
  }

  updateCompetition(newCompPartial: Partial<ICompetition>) {
    if (!this.state.competition) return;
    console.log(newCompPartial);
    this.setState({ loading: true });
    return CompetitionApi.updateCompetition({
      _id: this.state.competition._id,
      ...newCompPartial,
    })
      .then((competition) => this.setState({ competition, loading: false }))
      .catch((er) => this.setState({ error: er.message, loading: false }));
  }

  onPayrateChange(value) {
    this.updateCompetition({ [value.name]: value.value });
  }

  onColorPickerChanged(value) {
    this.setState({ editingColor: false });
    this.updateCompetition({ color: value.hex });
  }

  _handleTagKeyDown = (e) => {
    const ct = e.currentTarget;
    if (e.key === "Enter") {
      console.log("do validate");
      if (!this.state.competition) return;
      this.setState({ tagInputValue: "" });
      this.updateTags(ct.value);
    }
  };

  tagInputValueChanged(e) {
    this.setState({ tagInputValue: e.currentTarget.value });
  }

  removeTag(tag) {
    const compId = this.state.competitionId;
    if (!compId) return alert("An internal error occurred");
    this.setState({ loading: true, error: null });
    CompetitionApi.removeCompetitionTag(compId, tag)
      .then((comp) => this.setState({ competition: comp, loading: false }))
      .catch((er) => this.setState({ error: er.message, loading: false }));
  }

  updateTags(newTag: string) {
    const compId = this.state.competitionId;
    if (!compId) return alert("An internal error occurred");
    this.setState({ loading: true, error: null });
    CompetitionApi.addCompetitionTag(compId, newTag)
      .then((comp) => this.setState({ competition: comp, loading: false }))
      .catch((er) => this.setState({ error: er.message, loading: false }));
  }

  addNewPayrate() {
    let value = prompt("Competition mode: ");
    if (!value || value.length < 1) return;
    value = value.replace(/\W/g, "").toLowerCase();

    // this.onPayrateChange({ name: 'defaults.payrates.' + value, value: this.state.competition?.defaults?.payrates?.default })
  }

  public render() {
    if (!this.state.competitionId && !this.state.loading) {
      throw new Error(
        "CompetitionEditor did not receive prop 'competitionId' or found through url match",
      );
    }

    const { competition } = this.state;
    if (!competition) return <CenteredLoader />;

    const association = this.props.associations?.filter(
      (a) => a._id === competition.associationId,
    )?.[0];

    let payrates = [...(competition?.defaults?.payrates || [])];
    if (association.payrates) {
      payrates.push(
        ...association.payrates.map((p) => ({ ...p, inherited: true })),
      );
    }

    payrates = _.orderBy(payrates, (p) => p.priority || 100);
    if (this.state.showCurrentPayrates) {
      payrates = payrates.filter(
        (p) =>
          (!p.finish || toDateTime(p.finish) > DateTime.utc()) &&
          (!p.start || toDateTime(p.start) < DateTime.utc()),
      );
    }

    const prepareTextTag = (tag: ITag) =>
      tag.type === TagType.custom
        ? tag.value
        : `${TagType.toString(tag.type)}: ${tag.value}`;

    return (
      <div>
        <Card padding="xl">
          <h2>{competition.name}</h2>
          <AssociationSelector
            associations={this.props.associations}
            value={competition.associationId}
            onChange={console.log}
          />
          <p>
            <span>
              Color:{" "}
              <span
                style={{ backgroundColor: competition.color, padding: "5px" }}
              >
                {competition.color || <em>No color</em>}
              </span>{" "}
              <Popover
                opened={this.state.editingColor}
                onClose={() => this.setState({ editingColor: false })}
                position="bottom"
                shadow="sm"
              >
                <Popover.Target>
                  <button
                    onClick={() => this.setState({ editingColor: true })}
                  >
                    Edit
                  </button>
                </Popover.Target>
                <Popover.Dropdown style={{ padding: "20px" }}>
                  <CirclePicker
                    color={competition.color}
                    onChangeComplete={this.onColorPickerChanged}
                    colors={[
                      "#fff",
                      "#EB9694",
                      "#FAD0C3",
                      "#FEF3BD",
                      "#C1E1C5",
                      "#BEDADC",
                      "#C4DEF6",
                      "#BED3F3",
                      "#D4C4FB",
                    ]}
                  />
                </Popover.Dropdown>
              </Popover>
            </span>
          </p>
          <br />
          <h5>Payrates</h5>
          <p style={{ color: "darkgray" }}>
            Updating the payrate will affect all appointments made in the
            future. Adjusting the payrate on the competition means you may have
            to re-roster for these changes to be applied.
          </p>
          <p>
            <b>Payrates: </b>
          </p>
          {/* Show input group for "only show current payrates" */}
          <Stack gap="xs" mb="sm">
            <label>
              <input
                type="checkbox"
                name="showCurrentPayrates"
                checked={this.state.showCurrentPayrates}
                onChange={(e) => {
                  this.setState({
                    showCurrentPayrates: e.currentTarget.checked,
                  });
                }}
                style={{ marginTop: "0", marginRight: "10px" }}
              />
              Only show current payrates
            </label>
          </Stack>
          <SSTable>
            <thead>
              <tr>
                <th>Name</th>
                <th>Start</th>
                <th>Finish</th>
                <th>Priority</th>
                <th>Tags</th>
              </tr>
            </thead>
            <tbody>
              {payrates.map((attachment, idx) => {
                const style = {
                  color: attachment.inherited ? "darkgray" : "",
                };
                return (
                  <tr
                    key={`payrate-row-${idx}-${hashCode(
                      JSON.stringify(attachment),
                    )}}`}
                  >
                    <td style={style}>
                      {this.props.payrates[attachment.payrateId]?.name || (
                        <em>loading...</em>
                      )}
                    </td>
                    <td style={style}>
                      {toDateTimeNullable(attachment.start)?.toFormat(
                        "dd/MM/yyyy",
                      ) || ""}
                    </td>
                    <td style={style}>
                      {toDateTimeNullable(attachment.finish)?.toFormat(
                        "dd/MM/yyyy",
                      ) || ""}
                    </td>
                    <td style={style}>{attachment.priority || <em>100</em>}</td>
                    <td style={style}>
                      {attachment.tags?.map((t) => (
                        <span
                          key={t.type + t.value}
                          style={{
                            padding: "8px 12px",
                            marginRight: "5px",
                            borderRadius: "15px",
                            fontSize: "14px",
                            background: "#467bcf40",
                            color: "#467bcf",
                          }}
                          className="noselect"
                        >
                          {prepareTextTag(t)}
                        </span>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </SSTable>
          {/* {gameModes.map(mode => <div key={mode}>
            <p>{mode.toUpperCase()}{!(mode in compPayrates) && <em style={{ color: "darkgray" }}> (Inherited)</em>}</p>
            <PayratesSelector name={`defaults.payrates.${mode}`} value={payrates[mode]} onChange={this.onPayrateChange} />
          </div>)} */}

          <hr />
          <p>
            <b>Tags</b>
          </p>
          <Alert color="blue">
            Tags are used to help select payrates for appointments. For example, you
            can tag a competition with vjbl.
          </Alert>
          <TextInput
            onKeyDown={this._handleTagKeyDown}
            value={this.state.tagInputValue}
            onChange={(e) => this.tagInputValueChanged(e)}
            placeholder="Type a new tag here..."
          />
          <p style={{ marginTop: "16px" }}>
            {(competition.tags || [])
              .filter((t) => t.type == TagType.custom)
              .map((t) => (
                <span
                  key={t.type + t.value}
                  style={{
                    padding: "8px 12px",
                    marginRight: "5px",
                    borderRadius: "15px",
                    fontSize: "14px",
                    background: "#467bcf40",
                    color: "#467bcf",
                  }}
                  className="noselect"
                >
                  {prepareTextTag(t)}
                  <button
                    style={{
                      marginLeft: "5px",
                      paddingLeft: "2px",
                      paddingRight: "0",
                      borderRadius: "50%",
                      textAlign: "center",
                      border: "none",
                      background: "none",
                      color: "#467bcf",
                      fontWeight: "bold",
                    }}
                    onClick={() => this.removeTag(t.value)}
                  >
                    X
                  </button>
                </span>
              ))}
          </p>

          <hr />

          {this.state.loading && <CenteredLoader />}
          {this.state.error && <Alert color="red">{this.state.error}</Alert>}
        </Card>
      </div>
    );
  }
}

const mapState2Props = (state: IState, props: ICompetitionEditorProps) => ({
  associations: Object.values(state.associations.map),
  payrates: state.payrates.map,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({ getMyAssociations, getAllPayrates }, dispatch),
});

const MappedCompetitionEditor: React.FunctionComponent<
  ICompetitionEditorProps
> = (props) => {
  const { competitionId } = useParams();
  return <CompetitionEditor {...props} competitionId={competitionId} />;
};

export default connect(
  mapState2Props,
  mapDispatchToProps,
)(MappedCompetitionEditor);
